import { format } from 'date-fns';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { DateDomainService } from '@/domain/services';
import { redirect404Page } from '@/lib/AccessControl';
import {
  determineSelectedSiteId,
  formatTempHour,
  TempHourType,
} from '@/pages/camel-order/temp-hour/service';
import {
  TempHourFormError,
  validateTempHourDate,
} from '@/pages/camel-order/temp-hour/validate';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import {
  SiteListResponse,
  useSiteList,
} from '@/services/Site/hooks/list/useSiteList';
import {
  useTempHourCreate,
  useTempHourDelete,
  useTempHourDetail,
  useTempHourList,
  useTempHourUpdate,
} from '@/services/TempHour/hooks/useTempHoursAPI';
import {
  DeliveryStore,
  SITE_ID_QUERY_PARAM,
} from '@/services/TempHour/TempHourService';

export type formState = {
  error: TempHourFormError;
  sites: SiteListResponse[];
  deliveryStores: DeliveryStore[];
  selectedSiteId: string;
  tempHourDate: Date;
  tempHourType: TempHourType;
  tempHourStartHour: string;
  tempHourStartMinute: string;
  tempHourEndHour: string;
  tempHourEndMinute: string;
  deliveryStoreCheckboxState: DeliveryStoreCheckboxState;
  handleChangeSelectedSiteId: (siteId: string) => void;
  handleChangeTempHourDate: (date: Date) => void;
  handleChangeTempHourType: (tempHourType: TempHourType) => void;
  handleChangeTempHourStartHour: (tempHourStartHour: string) => void;
  handleChangeTempHourStartMinute: (tempHourStartMinute: string) => void;
  handleChangeTempHourEndHour: (tempHourEndHour: string) => void;
  handleChangeTempHourEndMinute: (tempHourEndMinute: string) => void;
  handleChangeDeliveryStoreCheckbox: (deliveryStoreId: string) => void;
  handleChangeAllDeliveryStoreCheckbox: () => void;
  isAllDeliveryStoresChecked: boolean;
};

type Return = {
  initialize: () => Promise<void>;
  handleCreate: () => Promise<void>;
  handleUpdate: () => Promise<void>;
  handleDelete: () => Promise<void>;
  isLoading: boolean;
  listUrl: string;
  isDisabledSubmit: boolean;
} & formState;

export type DeliveryStoreCheckboxState = {
  [deliveryStoreId: string]: boolean;
};

export const useTempHourForm = (id: string | undefined = undefined): Return => {
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  /* アクセス制御 */
  redirect404Page('camel_order_site_setting', history, managerAccountMe);

  const [tempHourDate, setTempHourDate] = useState<Date>(new Date());
  const [sites, setSites] = useState<SiteListResponse[]>([]);
  const [deliveryStores, setDeliveryStores] = useState<DeliveryStore[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [deliveryStoreCheckboxState, setDeliveryStoreCheckboxState] =
    useState<DeliveryStoreCheckboxState>({});
  const [tempHourType, setTempHourType] = useState<TempHourType>('close');
  const [tempHourStartHour, setTempHourStartHour] = useState<string>('');
  const [tempHourStartMinute, setTempHourStartMinute] = useState<string>('');
  const [tempHourEndHour, setTempHourEndHour] = useState<string>('');
  const [tempHourEndMinute, setTempHourEndMinute] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const errorObj = { message: '', isError: false, isTouched: false };
  const [error, setError] = useState<TempHourFormError>({
    tempHourDate: { ...errorObj },
  });
  const listUrl = new URL(routes.tempHourList.path, window.location.origin);
  if (selectedSiteId) {
    listUrl.searchParams.append(SITE_ID_QUERY_PARAM, selectedSiteId);
  }
  const listUrlString = listUrl.pathname + listUrl.search;

  const handleChangeSelectedSiteId = (siteId: string) => {
    setSelectedSiteId(siteId);
    loadDeliveryStoreList(siteId);
  };

  const handleChangeTempHourDate = (date: Date) => {
    setTempHourDate(date);
    setError(
      validateTempHourDate(
        error,
        tempHourType,
        date,
        tempHourStartHour,
        tempHourStartMinute,
        tempHourEndHour,
        tempHourEndMinute
      )
    );
  };

  const handleChangeTempHourType = (newType: TempHourType) => {
    setTempHourType(newType);
    setError(
      validateTempHourDate(
        error,
        newType,
        tempHourDate,
        tempHourStartHour,
        tempHourStartMinute,
        tempHourEndHour,
        tempHourEndMinute
      )
    );
  };

  const handleChangeTempHourStartHour = (hour: string) => {
    setTempHourStartHour(hour);
    setError(
      validateTempHourDate(
        error,
        tempHourType,
        tempHourDate,
        hour,
        tempHourStartMinute,
        tempHourEndHour,
        tempHourEndMinute
      )
    );
  };

  const handleChangeTempHourStartMinute = (minute: string) => {
    setTempHourStartMinute(minute);
    setError(
      validateTempHourDate(
        error,
        tempHourType,
        tempHourDate,
        tempHourStartHour,
        minute,
        tempHourEndHour,
        tempHourEndMinute
      )
    );
  };

  const handleChangeTempHourEndHour = (hour: string) => {
    setTempHourEndHour(hour);
    setError(
      validateTempHourDate(
        error,
        tempHourType,
        tempHourDate,
        tempHourStartHour,
        tempHourStartMinute,
        hour,
        tempHourEndMinute
      )
    );
  };

  const handleChangeTempHourEndMinute = (minute: string) => {
    setTempHourEndMinute(minute);
    setError(
      validateTempHourDate(
        error,
        tempHourType,
        tempHourDate,
        tempHourStartHour,
        tempHourStartMinute,
        tempHourEndHour,
        minute
      )
    );
  };

  const handleChangeDeliveryStoreCheckbox = (deliveryStoreId: string) => {
    setDeliveryStoreCheckboxState((prevState) => ({
      ...prevState,
      [deliveryStoreId]: !prevState[deliveryStoreId],
    }));
  };

  const getStartTimeEndTime = () => {
    let startTime = formatTempHour(
      tempHourDate,
      tempHourStartHour,
      tempHourStartMinute
    );
    let endTime = formatTempHour(
      tempHourDate,
      tempHourEndHour,
      tempHourEndMinute
    );
    if (tempHourType == 'close') {
      startTime = DateDomainService.yyyymmdd(tempHourDate) + '0000';
      endTime = DateDomainService.yyyymmdd(tempHourDate) + '2359';
    }
    return { startTime, endTime };
  };

  const handleCreate = async () => {
    const site = sites.find((site) => site.id === selectedSiteId);
    if (!site) return;
    const { startTime, endTime } = getStartTimeEndTime();
    try {
      setIsLoading(true);
      await useTempHourCreate(
        selectedSiteId,
        startTime,
        endTime,
        tempHourType === 'close',
        Object.keys(deliveryStoreCheckboxState).filter(
          (key) => deliveryStoreCheckboxState[key]
        )
      );
      setIsLoading(false);
      alert('登録しました。');
      history.push(listUrlString);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!id) {
      return;
    }
    const { startTime, endTime } = getStartTimeEndTime();
    try {
      setIsLoading(true);
      await useTempHourUpdate(
        id,
        startTime,
        endTime,
        tempHourType === 'close',
        Object.keys(deliveryStoreCheckboxState).filter(
          (key) => deliveryStoreCheckboxState[key]
        )
      );
      setIsLoading(false);
      alert('更新しました。');
      history.push(listUrlString);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) {
      return;
    }
    try {
      setIsLoading(true);
      await useTempHourDelete(id);
      setIsLoading(false);
      alert('削除しました。');
      history.push(listUrlString);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const setAllDeliveryStoreCheckboxState = (value: boolean) => {
    const checkboxState: DeliveryStoreCheckboxState = {};
    deliveryStores.forEach((store) => {
      checkboxState[store.id] = value;
    });
    setDeliveryStoreCheckboxState(checkboxState);
  };

  const isAllDeliveryStoresChecked = deliveryStores
    .map((store) => deliveryStoreCheckboxState[store.id])
    .every(Boolean);

  const handleChangeAllDeliveryStoreCheckbox = () => {
    setAllDeliveryStoreCheckboxState(!isAllDeliveryStoresChecked);
  };

  const initializeEditPageState = async (id: string) => {
    const tempHour = await useTempHourDetail(id);

    setTempHourType(tempHour.is_closed ? 'close' : 'open');
    const startDate = DateDomainService.parseNoColonFormat(tempHour.start_time);
    const endDate = DateDomainService.parseNoColonFormat(tempHour.end_time);
    const date = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate()
    );
    setTempHourDate(date);
    setTempHourStartHour(format(startDate, 'HH'));
    setTempHourStartMinute(format(startDate, 'mm'));
    setTempHourEndHour(format(endDate, 'HH'));
    setTempHourEndMinute(format(endDate, 'mm'));

    for (const deliveryStore of tempHour.delivery_stores) {
      setDeliveryStoreCheckboxState((prevState) => ({
        ...prevState,
        [deliveryStore.id]: true,
      }));
    }
    const checkboxState: DeliveryStoreCheckboxState = {};
    tempHour.delivery_stores.forEach((store) => {
      checkboxState[store.id] = true;
    });
    setDeliveryStoreCheckboxState(checkboxState);
  };

  const initialize = async () => {
    setIsLoading(true);
    const resSites = await useSiteList();
    setSites(resSites);
    if (resSites) {
      const siteId = determineSelectedSiteId(resSites, history);
      setSelectedSiteId(siteId);
      await loadDeliveryStoreList(siteId);
    }
    if (id) {
      await initializeEditPageState(id);
    }
    setIsLoading(false);
  };

  const loadDeliveryStoreList = async (siteId: string) => {
    const response = await useTempHourList(siteId);
    setDeliveryStores(response.delivery_stores);
    setDeliveryStoreCheckboxState({});
  };

  const checkedDeliveryStoreCount = Object.values(
    deliveryStoreCheckboxState
  ).filter(Boolean).length;

  const isDisabledSubmit = (() => {
    return (
      sites.length === 0 ||
      checkedDeliveryStoreCount === 0 ||
      error.tempHourDate.isError
    );
  })();

  return {
    sites,
    deliveryStores,
    selectedSiteId,
    tempHourDate,
    tempHourType,
    tempHourStartHour,
    tempHourStartMinute,
    tempHourEndHour,
    tempHourEndMinute,
    deliveryStoreCheckboxState,
    error,
    isLoading,
    initialize,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleChangeSelectedSiteId,
    handleChangeTempHourDate,
    handleChangeTempHourType,
    handleChangeTempHourStartHour,
    handleChangeTempHourStartMinute,
    handleChangeTempHourEndHour,
    handleChangeTempHourEndMinute,
    handleChangeAllDeliveryStoreCheckbox,
    handleChangeDeliveryStoreCheckbox,
    listUrl: listUrlString,
    isDisabledSubmit,
    isAllDeliveryStoresChecked,
  };
};
